<script>
import {defineComponent} from 'vue'
import api from "@/api/api"
import {formatIsoToCustom} from "@/common/formatToIso";
import {processObjectNullValues} from "@/common/processObjectNullValues";
import TicketLayouts from "@/components/Tickets/layouts/TicketLayouts.vue";

export default defineComponent({
  name: "TicketTypes",
  components: {TicketLayouts},
  data() {
    return {
      // pagination
      currentPage: 1,
      perPage: 15,
      perPageOptions: [
        {value: 10, text: '10 на странице'},
        {value: 15, text: '15 на странице'},
        {value: 25, text: '25 на странице'},
        {value: 50, text: '50 на странице'}
      ],
      meta: {
        current_page: 1,
        from: 0,
        last_page: 1,
        per_page: 15,
        to: 0,
        total: 0
      },
      links: {
        first: null,
        last: null,
        prev: null,
        next: null
      },
      //
      typeParams: {
        name: "",
        enabled: true,
      },
      search: '',
      ticketTypes: [],
      sidebarFields: [],
      isLoading: false,
      isSidebarLoading: false,
      selectedType: {},
      fields: [
        {key: 'id', label: '№',sortable: true},
        {key: 'date', label: 'Дата', sortable: true},
        {key: 'name', label: 'Название'},
        {key: 'enabled', label: 'Включено',},
        {key: 'username', label: 'Кем создан',},
        {key: "actions", label: "Действия"}
      ]
    }
  },
  computed: {
    rows() {
      const query = this.search.trim().toLowerCase()
      if (!query) return this.ticketTypes || []
      return (this.ticketTypes || []).filter(item =>
          [item.id, item.name].some(field => String(field ?? '').toLowerCase().includes(query)))
    },
  },
  methods: {
    async getTicketTypes(params = {page: this.currentPage, per_page: this.perPage}) {
      this.isLoading = true;
      try {
        const res = await api.tickets.getListTicketTypes(params)
        this.ticketTypes = res.data?.data.map((type) => processObjectNullValues({
          id: type.id,
          date: formatIsoToCustom(type.created_at),
          name: type.name,
          enabled: type.enabled ? 'Да' : "Нет",
          username: type.created_by?.username || type.created_by?.email,
        }));

        // Set pagination meta and links
        if (res.data?.meta) {
          this.meta = res.data.meta;
        }
        if (res.data?.links) {
          this.links = res.data.links;
        }

        this.isLoading = false;
      } catch (e) {
        console.log(e)
      } finally {
        this.isLoading = false;
      }
    },
    async createType() {
      if (this.typeParams.name === "") {
        this.$bvToast.toast('Введите название', {
          title: "Валидация",
          variant: "danger",
        })
        return false
      }
      try {
        const res = await api.tickets.createTicketType(this.typeParams)
        const newType = res.data?.data
        const formattedType = processObjectNullValues({
          id: newType.id,
          name: newType.name,
          date: formatIsoToCustom(newType.created_at),
          username: newType.created_by?.username || newType.created_by?.email,
          enabled: newType.enabled ? 'Да' : 'Нет',
          _original: newType
        })
        this.ticketTypes.push(formattedType)
        this.$bvModal.hide('create-type')
        this.typeParams.name = "";
      } catch (e) {
        console.log(e)
        this.$bvToast.toast(e.response.message || e.response.data.message || 'Произошла ошибка', {
          title: "Ошибка сервера",
          variant: "danger",
        })
      }
    },
    async openEditSidebar(item) {
      this.sidebarFields = [
        {
          key: 'name',
          label: 'Название',
          type: 'text',
          placeholder: 'Введите название',
          required: true
        },
        {
          key: 'enabled',
          label: 'Включено',
          type: 'checkbox',
          default: true
        }
      ]
      this.selectedType = {
        id: item.id,
        name: item.name,
        enabled: item.enabled
      }
      this.$root.$emit('bv::toggle::collapse', 'sidebar-right')
    },
    async handleSaveType(formData) {
      this.isSidebarLoading = true
      try {
        const res = await api.tickets.updateTicketType(this.selectedType?.id, {
          enabled: formData.enabled,
          name: formData.name
        })
        const updatedData = res.data?.data || formData
        const index = this.ticketTypes.findIndex(type => type.id === formData.id)
        if (index !== -1) {
          const oldItem = this.ticketTypes[index]
          this.$set(this.ticketTypes, index, processObjectNullValues({
            id: updatedData.id,
            date: oldItem.date,
            name: updatedData.name,
            enabled: updatedData.enabled ? 'Да' : 'Нет',
            username: oldItem.username,
            _original: updatedData
          }))
        }
        this.$bvToast.toast('Тип успешно обновлена', {
          title: "Успех",
          variant: "success",
        })
        this.$root.$emit('bv::toggle::collapse', 'sidebar-right')
        this.selectedType = null
      } catch (e) {
        console.log(e)
        this.$bvToast.toast(e.response?.message || e.response?.data?.message || 'Произошла ошибка', {
          title: "Ошибка сервера",
          variant: "danger",
        })
      } finally {
        this.isSidebarLoading = false;
      }
    },
    async deleteType(type) {
      try {
        if (type && type.id) {
          await api.tickets.deleteTicketType(type?.id)
        }
        this.ticketTypes = this.ticketTypes.filter(item => item.id !== type.id)
      } catch (e) {
        console.log('deleting type error: ', e)
        this.$bvToast.toast(e.response.message || e.response.data.message || 'Произошла ошибка', {
          title: "Ошибка сервера",
          variant: "danger",
        })
      }
    },
    onPageChange(page) {
      this.currentPage = page
      this.getTicketTypes({page: page, per_page: this.perPage})
      window.scrollTo({top: 0, behavior: 'smooth'})
    },
    changePerPage() {
      this.currentPage = 1
      this.getTicketTypes({page: 1, per_page: this.perPage})
    }
  },
  watch: {
    'meta.current_page': {
      handler(newPage) {
        if (newPage && this.currentPage !== newPage) {
          this.currentPage = newPage
        }
      }
    }
  },
  mounted() {
    this.getTicketTypes({page: this.currentPage, per_page: this.perPage})
  }
})
</script>

<template>
  <TicketLayouts title="Редактировать тип"
                 :sidebar-fields="sidebarFields"
                 :sidebar-item="selectedType"
                 :sidebar-loading="isSidebarLoading"
                 @sidebar-save="handleSaveType"
  >
    <div class="tr">
      <Breadcrumbs main="Тикеты" title="Типы обращений"/>

      <div class="container-fluid tr-wrap">
        <div class="tr-toolbar">
          <div class="tr-search">
            <i class="fa fa-search tr-search__icon"></i>
            <input type="search" class="tr-search__input" v-model="search"
                   placeholder="ID или название типа…" @keydown.esc.prevent="search = ''"/>
            <button v-if="search" type="button" class="tr-search__clear" @click="search = ''">
              <i class="fa fa-times"></i>
            </button>
          </div>

          <span class="tr-spacer"></span>

          <button type="button" class="tr-icon-btn" :class="{ 'is-spin': isLoading }"
                  @click="getTicketTypes()" v-b-tooltip.hover.bottom="'Обновить'">
            <i class="fa fa-refresh"></i>
          </button>

          <button type="button" class="tr-btn-primary" v-b-modal.create-type>
            <i class="fa fa-plus"></i> Создать тип
          </button>
        </div>

        <div class="tr-panel">
          <div v-if="isLoading" class="tr-skeleton">
            <div v-for="n in 5" :key="'sk' + n" class="tr-skeleton__row">
              <span class="tr-sk"></span><span class="tr-sk"></span>
              <span class="tr-sk"></span><span class="tr-sk"></span><span class="tr-sk"></span>
            </div>
          </div>

          <div v-else-if="!rows.length" class="tr-empty">
            <div class="tr-empty__art"><i class="fa fa-sitemap"></i></div>
            <div class="tr-empty__title">{{ search ? 'Ничего не нашлось' : 'Типов нет' }}</div>
            <div class="tr-empty__text">
              {{ search ? 'Попробуйте изменить запрос.' : 'Тип задаёт, какие категории доступны в обращении.' }}
            </div>
            <button type="button" class="tr-btn-primary" v-b-modal.create-type>
              <i class="fa fa-plus"></i> Создать тип
            </button>
          </div>

          <div v-else class="tr-table-wrap">
            <table class="tr-table">
              <thead>
              <tr>
                <th>ID</th>
                <th>Название</th>
                <th>Включён</th>
                <th>Кем создан</th>
                <th>Создан</th>
                <th></th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="item in rows" :key="'tp' + item.id">
                <td><span class="tr-id">#{{ item.id }}</span></td>
                <td><span class="tr-name">{{ item.name }}</span></td>
                <td>
                  <span class="tr-badge" :class="item.enabled === 'Да' ? 'tr-badge--ok' : 'tr-badge--off'">
                    {{ item.enabled === 'Да' ? 'Включён' : 'Выключен' }}
                  </span>
                </td>
                <td>{{ item.username }}</td>
                <td class="tr-num">{{ item.date }}</td>
                <td>
                  <div class="tr-actions">
                    <button type="button" class="tr-act" @click="openEditSidebar(item)"
                            v-b-tooltip.hover.top="'Редактировать'">
                      <i class="fa fa-pencil"></i>
                    </button>
                    <button type="button" class="tr-act tr-act--danger" @click="deleteType(item)"
                            v-b-tooltip.hover.top="'Удалить'">
                      <i class="fa fa-trash-o"></i>
                    </button>
                  </div>
                </td>
              </tr>
              </tbody>
            </table>
          </div>

          <div v-if="!isLoading && rows.length" class="tr-foot">
            <div class="tr-foot__info">
              Показано с <b>{{ meta.from }}</b> по <b>{{ meta.to }}</b> из <b>{{ meta.total }}</b>
            </div>
            <div class="d-flex align-items-center">
              <b-form-select v-model="perPage" :options="perPageOptions" size="sm"
                             @change="changePerPage" class="mr-2" style="width: 90px"/>
              <b-pagination class="tr-pagination my-0" v-model="currentPage" :total-rows="meta.total"
                            :per-page="meta.per_page" :limit="7" @change="onPageChange" size="sm"/>
            </div>
          </div>
        </div>
      </div>
    </div>

    <b-modal id="create-type" title="Новый тип" hide-footer>
      <b-form @submit.prevent="createType">
        <label class="font-weight-bold">Название</label>
        <b-form-input v-model="typeParams.name" placeholder="Например: Финансовый"/>
        <b-form-checkbox class="mt-3" v-model="typeParams.enabled">Включён</b-form-checkbox>
        <b-button class="mt-3 w-100" variant="primary" type="submit">Создать</b-button>
      </b-form>
    </b-modal>
  </TicketLayouts>
</template>

<style scoped lang="scss">
@import "~@/assets/scss/ticket-ref.scss";
</style>
